import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  getGetAutomationQueryKey,
  getListAutomationsQueryKey,
  getQueryAutomationQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  useDeleteDraftAutomation,
  useQueryAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import { useListRuns } from '@/generated/hooks/jeeves/v1/api';
import { V1InvocationSource } from '@/generated/hooks/jeeves/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { AssetDeleteDialog } from '@/layouts/AssetCatalogLayout';
import { FilterBuilder } from '@/shared/utils';
import { RUN_STAGE, TERMINAL_RUN_STATES } from '@/shared/constants';
import type { IDraftDeleteDialogProps } from '../types';

export function DraftDeleteDialog({
  open,
  onClose,
  title,
  draftId,
  onDelete,
  currentVersion,
  isDiscard = false,
}: IDraftDeleteDialogProps) {
  const { t } = useTranslation('drafts');
  const queryClient = useQueryClient();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    mutate: deleteAutomation,
    isPending: isDeleteAutomationPending,
    error: deleteAutomationError,
    reset: resetMutation,
  } = useDeleteDraftAutomation();

  // The active-run warning must only consider runs of THIS draft version
  // — a freshly forked draft (e.g. 2.x) should not be blocked by a
  // non-terminal run left over from a prior draft version (1.x) on the
  // same automation lineage (OC-1282 / #416).
  //
  // Prefer the version the caller already has (the drafts list keeps it
  // fresh: staleTime 0 + refetchOnMount). Only fetch the draft ourselves
  // when the caller did not pass it, so we never break for callers that
  // can't supply it.
  const { data: latestDraftData } = useQueryAutomation(
    currentOrganizationId,
    currentWorkspaceId,
    draftId,
    { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
    {
      query: {
        enabled: open && Boolean(draftId) && !currentVersion,
        refetchOnMount: 'always',
      },
    },
  );

  const draftVersion = currentVersion ?? latestDraftData?.data?.version;

  // Detect whether this draft has any non-terminal (active) run when the
  // dialog is open. The query is disabled while the dialog is closed so
  // we don't waste requests on every card mount, and is scoped to the
  // current draft's stage_version so prior-version runs don't drive the
  // warning.
  const {
    data: recentRunsData,
    refetch: refetchRecentRuns,
    isFetching: isRecentRunsFetching,
  } = useListRuns(
    currentOrganizationId,
    currentWorkspaceId,
    draftId,
    {
      page_size: 1,
      // Scope to non-terminal (active) runs of THIS draft version, so any
      // returned run means the draft still has work in flight. Terminal runs
      // are excluded server-side via `runState != <state>` for each terminal
      // state (the API rejects `NOT (...)`, so we chain not-equals instead).
      filter: TERMINAL_RUN_STATES.reduce(
        (builder, state) => builder.notEquals('runState', state),
        FilterBuilder.create()
          .equals('stage', RUN_STAGE.DRAFT)
          .equals('stage_version', draftVersion || '')
          .notEquals(
            'invocation_source',
            V1InvocationSource.INVOCATION_SOURCE_TEST,
          ),
      ).build(),
    },
    {
      query: {
        enabled: open && Boolean(draftId) && Boolean(draftVersion),
        // Always fetch fresh run state when the query runs; combined with the
        // refetch-on-open effect below this re-checks every time the dialog opens.
        staleTime: 0,
      },
    },
  );

  // The dialog stays mounted while the card menu is open, so `open` toggling
  // to true won't remount the query. Refetch explicitly on each open so the
  // active-run check reflects current state every time the user opens it.
  useEffect(() => {
    if (open && draftId && draftVersion) {
      refetchRecentRuns();
    }
  }, [open, draftId, draftVersion, refetchRecentRuns]);

  // The query already filters to non-terminal runs of this draft version, so
  // any returned run means an active run exists.
  const hasActiveRun = (recentRunsData?.data?.runs ?? []).length > 0;

  const handleClose = () => {
    resetMutation();
    onClose();
  };

  const handleDelete = () => {
    deleteAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: draftId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          // Marked stale without refetching: the open editor would 404 before it closes.
          for (const queryKey of [
            getGetAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              draftId,
            ),
            getQueryAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              draftId,
            ),
          ]) {
            queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
          }
          handleClose();
          onDelete?.();
        },
      },
    );
  };

  return (
    <AssetDeleteDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleDelete}
      title={
        isDiscard
          ? t('drafts.deleteDialog.discard.title')
          : t('drafts.deleteDialog.title')
      }
      description={
        isDiscard
          ? t('drafts.deleteDialog.discard.description', { draftTitle: title })
          : t('drafts.deleteDialog.description', { draftTitle: title })
      }
      confirmation={t('drafts.deleteDialog.confirmation')}
      warning={
        hasActiveRun
          ? {
              title: t('drafts.deleteDialog.activeRunWarning.title'),
              description: t(
                'drafts.deleteDialog.activeRunWarning.description',
              ),
            }
          : undefined
      }
      confirmLabel={
        isDiscard
          ? t('drafts.deleteDialog.discard.action')
          : t('drafts.deleteDialog.actions.delete')
      }
      isPending={isDeleteAutomationPending}
      isCheckingPreconditions={isRecentRunsFetching}
      error={deleteAutomationError}
      errorMessage={t('drafts.deleteDialog.error')}
      data-testid="draft-delete-dialog"
    />
  );
}
